// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { ModifiedTrainingFacetValue } from "@werner/App/Services/EventService";
import { CustomAppState } from "@werner/App/Store/Reducers";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface TrainingCalenderListFiltersAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
}

export const trainingCalenderListFiltersAccordionSectionStyles: TrainingCalenderListFiltersAccordionSectionStyles = {
    accordionSection: {
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            &:not(:last-child) {
                padding-bottom: 5px;
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                ${({ _sizeVariant }) =>
                    _sizeVariant === "small"
                        ? css`
                              margin-top: 5px;
                          `
                        : css`
                              margin-top: 4px;
                          `}
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
            weight: "bold",
        },
    },
    seeAllLink: {
        css: css`
            padding-top: 5px;
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 5px;
        `,
    },
};

export interface Props {
    title: string;
    facetValues: ModifiedTrainingFacetValue[];
    onChangeFacet: (facet: ModifiedTrainingFacetValue) => void;
    showMoreLimit: number;
    extendedStyles?: TrainingCalenderListFiltersAccordionSectionStyles;
    expandByDefault: boolean;
    selectedFilters: any;
}

const TrainingCalenderListFiltersAccordionSection: FC<Props> = ({
    title,
    facetValues,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
    selectedFilters,
}) => {
    const [styles] = React.useState(() =>
        mergeToNew(trainingCalenderListFiltersAccordionSectionStyles, extendedStyles),
    );
    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facetValues : facetValues?.slice(0, showMoreLimit);

    return (
        <ManagedAccordionSection title={title} {...styles.accordionSection} initialExpanded={expandByDefault}>
            {limitedFacets?.map((facet, index) => (
                <CheckboxGroup key={facet.Id} {...styles.checkBoxGroup}>
                    <Checkbox
                        checked={selectedFilters[facet.parent] === facet.Name}
                        onChange={() => {
                            onChangeFacet(facet);
                        }}
                        data-test-selector={`facetFilter${facet.Id}`}
                    >
                        {facet.Name}
                    </Checkbox>
                </CheckboxGroup>
            ))}
            {facetValues &&
                facetValues.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)} aria-label="See Less">
                        {translate("See Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)} aria-label="See All">
                        {translate("See All")}
                    </Link>
                ))}
        </ManagedAccordionSection>
    );
};

export default TrainingCalenderListFiltersAccordionSection;
